import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { QuotesService } from '../quotes/quotes.service';
import { QuoteStatus } from '../quotes/quotes.state.interface';

import { OrdersActions } from './orders.actions';
import {
  OrderClient,
  OrderExportDetails,
  SingleList,
  SingleListPUTApiResponse,
} from './orders.model';
import { OrdersService } from './orders.service';

export const loadOrder$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrder),
      switchMap(({ id, isEdit }) =>
        ordersService.getOrderById(id, isEdit).pipe(
          map((order) => OrdersActions.loadOrderSuccess({ order })),
          catchError((error) => of(OrdersActions.loadOrderFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const addOrderNote$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.addOrderNote),
      switchMap(({ orderId, note }) =>
        ordersService.addOrderNote(orderId, note).pipe(
          map((noteObj) => OrdersActions.addOrderNoteSuccess({ orderId, noteObj })),
          catchError((error) => of(OrdersActions.addOrderNoteFailure({ orderId, error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadOrderAssignees$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrderAssignees),
      switchMap(({ payload }) =>
        quotesService.getAssignees(payload).pipe(
          map((assignees) => OrdersActions.loadOrderAssigneesSuccess({ assignees })),
          catchError((error) =>
            of(OrdersActions.loadOrderAssigneesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadOrderStatuses$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrderStatuses),
      switchMap(({ payload }) =>
        quotesService.getStatuses(payload).pipe(
          map((statuses: QuoteStatus[]) => OrdersActions.loadOrderStatusesSuccess({ statuses })),
          catchError((error) =>
            of(OrdersActions.loadOrderStatusesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadOrderClients$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrderClients),
      switchMap(({ payload }) =>
        ordersService.getClients(payload).pipe(
          map((clients: OrderClient[]) => OrdersActions.loadOrderClientsSuccess({ clients })),
          catchError((error) =>
            of(OrdersActions.loadOrderClientsFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadOrderMaterialsCategories$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrderMaterialsCategories),
      switchMap(({ payload }) =>
        quotesService.getStatuses(payload).pipe(
          map((categories: QuoteStatus[]) =>
            OrdersActions.loadOrderMaterialsCategoriesSuccess({ categories }),
          ),
          catchError((error) =>
            of(OrdersActions.loadOrderMaterialsCategoriesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadOrderMaterialsStatus$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrderMaterialsStatus),
      switchMap(({ payload }) =>
        quotesService.getStatuses(payload).pipe(
          map((statuses: QuoteStatus[]) =>
            OrdersActions.loadOrderMaterialsStatusSuccess({ statuses }),
          ),
          catchError((error) =>
            of(OrdersActions.loadOrderMaterialsStatusFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadPackingSlipCategories$ = createEffect(
  (actions$ = inject(Actions), quotesService = inject(QuotesService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadPackingSlipCategories),
      switchMap(({ payload }) =>
        quotesService.getStatuses(payload).pipe(
          map((categories: QuoteStatus[]) =>
            OrdersActions.loadPackingSlipCategoriesSuccess({ categories }),
          ),
          catchError((error) =>
            of(OrdersActions.loadPackingSlipCategoriesFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const saveOrder$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.saveOrder),
      switchMap(({ order, initiateRedirect }) =>
        ordersService.updateOrder(order.id, order).pipe(
          map((updatedOrder) =>
            OrdersActions.saveOrderSuccess({ order: updatedOrder, initiateRedirect }),
          ),
          catchError((error) => of(OrdersActions.saveOrderFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const saveOrderSuccess = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.saveOrderSuccess),
      tap(({ order }) => {
        snackBar.open('Order successfully updated.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });

        router.navigate([`/orders/${order.id}/view`]).then();
      }),
    ),
  { functional: true, dispatch: false },
);

export const navigateOnSaveSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(OrdersActions.saveOrderSuccess),
      tap(({ order, initiateRedirect }) => {
        if (initiateRedirect) {
          router.navigate([`/orders/${order.id}/view`]).then((success: boolean): void => {
            if (success) {
              console.log('Navigated successfully!');
            } else {
              console.warn('Navigation failed!');
            }
          });
        }
      }),
    ),
  { functional: true, dispatch: false },
);

export const showSaveError$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.saveOrderFailure),
      tap(({ error }) => {
        let message = 'Failed to save order. Please try again.';
        // The actual errors are in error.error.errors
        const errorsObj = error?.error?.errors;
        if (errorsObj && typeof errorsObj === 'object' && Object.keys(errorsObj).length > 0) {
          message = Object.values(errorsObj).join('\n');
        }
        snackBar.open(message, 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error', 'whitespace-pre-line'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadOrdersList$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrdersList),
      switchMap(({ payload }) =>
        ordersService.getOrders(payload).pipe(
          map((response) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const priceTotal = parseFloat(response.headers?.get('x-price-total') || '0');
            const priceTotalNotShipped = parseFloat(
              response.headers?.get('x-price-total-not-shipped') || '0',
            );
            const orders = response.body || [];

            return OrdersActions.loadOrdersListSuccess({
              orders,
              totalCount: +totalCount,
              priceTotal,
              priceTotalNotShipped,
            });
          }),
          catchError((error) => of(OrdersActions.loadOrdersListFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadMyOrders$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadMyOrders),
      switchMap(({ payload }) =>
        ordersService.getOrders(payload).pipe(
          map((response) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const totalPrice = response.headers?.get('x-price-total') || 0;
            const orders = response.body || [];
            return OrdersActions.loadMyOrdersSuccess({
              orders,
              totalCount: +totalCount,
              totalPrice: +totalPrice,
            });
          }),
          catchError((error) => of(OrdersActions.loadMyOrdersFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadPastDueOrders$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadPastDueOrders),
      switchMap(({ payload }) =>
        ordersService.getPastDueOrders(payload).pipe(
          map((response) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const totalPrice = response.headers?.get('x-price-total') || 0;
            const orders = response.body || [];

            return OrdersActions.loadPastDueOrdersSuccess({
              orders,
              totalCount: +totalCount,
              totalPrice: +totalPrice,
            });
          }),
          catchError((error) =>
            of(OrdersActions.loadPastDueOrdersFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const loadOrdersDueToday$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadOrdersDueToday),
      switchMap(({ payload }) =>
        ordersService.getPastDueOrders(payload).pipe(
          map((response) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            const totalPrice = response.headers?.get('x-price-total') || 0;
            const orders = response.body || [];

            return OrdersActions.loadOrdersDueTodaySuccess({
              orders,
              totalCount: +totalCount,
              totalPrice: +totalPrice,
            });
          }),
          catchError((error) =>
            of(OrdersActions.loadOrdersDueTodayFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const archiveOrder$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.archiveOrder),
      switchMap(({ orderId, isArchived }) =>
        ordersService.archiveOrder(orderId, isArchived).pipe(
          map(() => OrdersActions.archiveOrderSuccess({ orderId, isArchived })),
          catchError((error) => of(OrdersActions.archiveOrderFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const showArchiveError$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.archiveOrderFailure),
      tap(({ error }) => {
        snackBar.open('Failed to update order archive status. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const deleteOrder$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.deleteOrder),
      switchMap(({ orderId }) =>
        ordersService.deleteOrder(orderId).pipe(
          map(() => OrdersActions.deleteOrderSuccess({ orderId })),
          catchError((error) => of(OrdersActions.deleteOrderFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const showDeleteError$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.deleteOrderFailure),
      tap(({ error }) => {
        snackBar.open('Failed to delete order. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const createOrder$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.createOrder),
      switchMap(({ payload }) =>
        ordersService.createOrder(payload).pipe(
          map((order) => OrdersActions.createOrderSuccess({ order })),
          catchError((error) => of(OrdersActions.createOrderFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

// export const createOrderSuccess$ = createEffect(
//   (actions$ = inject(Actions), router = inject(Router), dialog = inject(MatDialog)) =>
//     actions$.pipe(
//       ofType(OrdersActions.createOrderSuccess),
//       tap(({ order }) => {
//         dialog.closeAll();
//         router.navigate([`/orders/${order.id}/view`]).then();
//       }),
//     ),
//   { functional: true, dispatch: false },
// );

export const loadLists$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) => {
    return actions$.pipe(
      ofType(OrdersActions.loadLists),
      switchMap(({ payload }) =>
        ordersService.getLists(payload).pipe(
          map((response) => {
            const totalCount = response.headers?.get('x-total-count') || 0;
            return OrdersActions.loadListsSuccess({
              payload: {
                lists: response.body || [],
                totalCount: +totalCount,
              },
            });
          }),
          catchError((error) => of(OrdersActions.loadListsFailure({ error: error.message }))),
        ),
      ),
    );
  },
  { functional: true },
);

export const loadCustomerCodes$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadCustomerCodes),
      switchMap(({ payload }) =>
        ordersService.getCustomerCodes(payload).pipe(
          map((customerCodes) => {
            const codesArray = Array.isArray(customerCodes)
              ? customerCodes
              : Object.values(customerCodes);
            // Ensure only strings are included
            const stringCodes: string[] = codesArray.filter(
              (c): c is string => typeof c === 'string',
            );
            return OrdersActions.loadCustomerCodesSuccess({ customerCodes: stringCodes });
          }),
          catchError((error) => of(OrdersActions.loadCustomerCodesFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const loadSingleList$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.loadSingleList),
      switchMap(({ id, payload }) =>
        ordersService.getSingleList(id, payload).pipe(
          map((singleList: SingleList) => OrdersActions.loadSingleListSuccess({ singleList })),
          catchError((error) => of(OrdersActions.loadSingleListFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateSingleList$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.updateSingleList),
      switchMap(({ id, payload }) =>
        ordersService.updateSingleList(id, payload).pipe(
          map((response: SingleListPUTApiResponse) =>
            OrdersActions.updateSingleListSuccess({ response }),
          ),
          catchError((error) => of(OrdersActions.updateSingleListFailure({ error }))),
        ),
      ),
    ),
  { functional: true },
);

export const updateSingleListSuccess$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.updateSingleListSuccess),
      tap(() => {
        snackBar.open('List of orders successfully updated.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const deleteList$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService), router = inject(Router)) =>
    actions$.pipe(
      ofType(OrdersActions.deleteList),
      switchMap(({ id }) =>
        ordersService.deleteList(id).pipe(
          map(() => OrdersActions.deleteListSuccess({ id })),
          catchError((error) => of(OrdersActions.deleteListFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const deleteListSuccess$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.deleteListSuccess),
      tap(() => {
        snackBar.open('List successfully deleted.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const navigateOnDeleteListSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router)) =>
    actions$.pipe(
      ofType(OrdersActions.deleteListSuccess),
      tap(() => {
        router.navigate(['/orders', 'saved-lists']);
      }),
    ),
  { functional: true, dispatch: false },
);

export const showDeleteListError$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.deleteListFailure),
      tap(({ error }) => {
        snackBar.open('Failed to delete list. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const exportList$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.exportList),
      switchMap(({ id, exportFormat }) =>
        ordersService.exportList(id, exportFormat).pipe(
          map((blob) => {
            // Create a download link and trigger the download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `list-${id}.${exportFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return OrdersActions.exportListSuccess({ id, exportFormat });
          }),
          catchError((error) => of(OrdersActions.exportListFailure({ error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);

export const showExportError$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.exportListFailure),
      tap(({ error }) => {
        snackBar.open('Failed to export list. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const createSingleList$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.createSingleList),
      switchMap(({ payload }) =>
        ordersService.createSingleList(payload).pipe(
          map((response) => OrdersActions.createSingleListSuccess({ response })),
          catchError((error) =>
            of(OrdersActions.createSingleListFailure({ error: error.message })),
          ),
        ),
      ),
    ),
  { functional: true },
);

export const createSingleListSuccess$ = createEffect(
  (actions$ = inject(Actions), router = inject(Router), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.createSingleListSuccess),
      tap(({ response }) => {
        snackBar.open('List of orders successfully added.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });
        router.navigate(['/orders', response.id, 'view-list']);
      }),
    ),
  { functional: true, dispatch: false },
);

export const createSingleListFailure$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.createSingleListFailure),
      tap(({ error }) => {
        snackBar.open('Failed to create list. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const loadExportDetails$ = createEffect(
  (
    actions$ = inject(Actions),
    ordersService = inject(OrdersService),
    snackBar = inject(MatSnackBar),
  ) =>
    actions$.pipe(
      ofType(OrdersActions.loadExportDetails),
      switchMap(({ orderId }) =>
        ordersService.getOrderExportDetails(orderId).pipe(
          map((exportDetails: OrderExportDetails) =>
            OrdersActions.loadExportDetailsSuccess({ exportDetails }),
          ),
          catchError((error) => {
            snackBar.open('Failed to load export details. Please try again.', 'Close', {
              duration: 5000,
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom',
            });
            return of(OrdersActions.loadExportDetailsFailure({ error }));
          }),
        ),
      ),
    ),
  { functional: true },
);

export const exportOrder$ = createEffect(
  (
    actions$ = inject(Actions),
    ordersService = inject(OrdersService),
    snackBar = inject(MatSnackBar),
  ) =>
    actions$.pipe(
      ofType(OrdersActions.exportOrder),
      switchMap(({ orderId, payload }) =>
        ordersService.exportOrder(orderId, payload).pipe(
          map((response) => OrdersActions.exportOrderSuccess({ response })),
          catchError((error) => {
            snackBar.open('Failed to export order. Please try again.', 'Close', {
              duration: 5000,
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom',
            });
            return of(OrdersActions.exportOrderFailure({ error }));
          }),
        ),
      ),
    ),
  { functional: true },
);

export const exportOrderSuccess$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.exportOrderSuccess),
      tap(({ response }) => {
        if (response?.url) {
          window.open(response.url, '_blank');
        }
        snackBar.open('Export successful!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });
        // If you want to close the dialog, use a shared service or event here
      }),
    ),
  { functional: true, dispatch: false },
);

export const exportOrderFailure$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.exportOrderFailure),
      tap(() => {
        snackBar.open('Export failed. Please try again.', 'Close', {
          duration: 5000,
          panelClass: ['snackbar-error'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const exportOrdersBundle$ = createEffect(
  (
    actions$ = inject(Actions),
    ordersService = inject(OrdersService),
    snackBar = inject(MatSnackBar),
  ) =>
    actions$.pipe(
      ofType(OrdersActions.exportOrdersBundle),
      switchMap(({ payload }) =>
        ordersService.exportOrdersBundle(payload).pipe(
          map((response) => OrdersActions.exportOrdersBundleSuccess({ response })),
          catchError((error) => {
            snackBar.open('Failed to export orders bundle. Please try again.', 'Close', {
              duration: 5000,
              panelClass: ['snackbar-error'],
              verticalPosition: 'bottom',
            });
            return of(OrdersActions.exportOrdersBundleFailure({ error }));
          }),
        ),
      ),
    ),
  { functional: true },
);

export const exportOrdersBundleSuccess$ = createEffect(
  (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) =>
    actions$.pipe(
      ofType(OrdersActions.exportOrdersBundleSuccess),
      tap(({ response }) => {
        if (response?.url) {
          window.open(response.url, '_blank');
        }
        snackBar.open('Export orders bundle successful!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
          verticalPosition: 'bottom',
        });
      }),
    ),
  { functional: true, dispatch: false },
);

export const setUnlock$ = createEffect(
  (actions$ = inject(Actions), ordersService = inject(OrdersService)) =>
    actions$.pipe(
      ofType(OrdersActions.setUnlock),
      switchMap(({ id }) =>
        ordersService.unlockOrder(id).pipe(
          map(() => OrdersActions.setUnlockSuccess({ id })),
          catchError((error) => of(OrdersActions.setUnlockFailure({ id, error: error.message }))),
        ),
      ),
    ),
  { functional: true },
);
